import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { SectionCard } from '@/components/ui';
import type { ChannelBreakdown } from '../types/dashboardWidget';

const CHANNEL_COLORS: Record<string, string> = {
  WEB_STORE: 'var(--accent-default)',
  MOBILE: 'var(--signal-info)',
  B2B: 'var(--signal-success)',
  PARTNER: 'var(--signal-warn)',
};

const DEFAULT_COLOR = 'var(--content-soft)';

interface ChannelDonutChartProps {
  data?: ChannelBreakdown[] | undefined;
  isLoading?: boolean | undefined;
  title: string;
  channelLabels: Record<string, string>;
}

export const ChannelDonutChart = ({
  data,
  isLoading,
  title,
  channelLabels,
}: ChannelDonutChartProps) => {
  const chartData = (data ?? [])
    .map((d) => ({
      name: d.channel ? (channelLabels[d.channel] ?? d.channel) : '기타',
      value: d.amount ?? 0,
      ratio: d.ratio ?? 0,
      channel: d.channel ?? 'UNKNOWN',
    }))
    .filter((d) => d.value > 0);

  const isEmpty = !isLoading && chartData.length === 0;

  return (
    <SectionCard title={title}>
      {isLoading || isEmpty ? (
        <div className="flex h-48 items-center justify-center">
          <span
            className="text-sm"
            style={{ color: 'var(--content-muted)' }}
          >
            {isLoading ? '—' : '데이터 없음'}
          </span>
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={200}>
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              innerRadius={52}
              outerRadius={80}
              dataKey="value"
              strokeWidth={2}
              stroke="var(--surface-raised, #fff)"
              isAnimationActive={true}
              animationDuration={800}
            >
              {chartData.map((entry) => (
                <Cell
                  key={entry.channel}
                  fill={CHANNEL_COLORS[entry.channel] ?? DEFAULT_COLOR}
                />
              ))}
            </Pie>
            <Tooltip
              formatter={(value, _name, entry) => [
                `${new Intl.NumberFormat('ko-KR').format(Number(value))}원 (${((entry.payload as { ratio?: number }).ratio ?? 0).toFixed(1)}%)`,
              ]}
              contentStyle={{
                background: 'var(--surface-raised)',
                border: '1px solid var(--divider-strong)',
                borderRadius: 'var(--radius-card)',
                fontSize: 12,
              }}
            />
            <Legend
              iconType="circle"
              iconSize={8}
              wrapperStyle={{ fontSize: 11 }}
            />
          </PieChart>
        </ResponsiveContainer>
      )}
    </SectionCard>
  );
};
